import { useEffect, useMemo, useState } from "react";
import { DownloadSimple, X, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const DISMISS_KEY = "duemate.install.dismissed_at";
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// DueMate brand palette for the burst
const CONFETTI_COLORS = ["#4361EE", "#4ECDC4", "#FFD166", "#FF6B6B", "#111111"];

function celebrateInstall() {
  try {
    const end = Date.now() + 700;
    const shoot = () => {
      confetti({
        particleCount: 40,
        spread: 70,
        startVelocity: 35,
        origin: { y: 0.3 },
        colors: CONFETTI_COLORS,
        scalar: 0.9,
        ticks: 200,
        disableForReducedMotion: true,
      });
      if (Date.now() < end) requestAnimationFrame(shoot);
    };
    shoot();
    toast.success("You're all set!", {
      description: "DueMate is installed on your device.",
      duration: 3500,
    });
  } catch {}
}

// --- Environment detection helpers ------------------------------------------------

function isStandalone() {
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    if (typeof navigator !== "undefined" && navigator.standalone === true) return true; // iOS
  } catch {}
  return false;
}

function isIOS() {
  const ua = navigator.userAgent || navigator.vendor || "";
  // iPhone/iPod: "iPhone|iPod". iPad on iOS 13+ reports as MacIntel with touch — check maxTouchPoints.
  if (/iPad|iPhone|iPod/i.test(ua) && !window.MSStream) return true;
  if (/Macintosh/i.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1) return true;
  return false;
}

function isIOSSafari() {
  if (!isIOS()) return false;
  const ua = navigator.userAgent || "";
  // Exclude in-app browsers where install isn't possible even on iOS
  if (/CriOS|FxiOS|EdgiOS|OPiOS|GSA|FBAN|FBAV|Instagram|Line|Twitter/i.test(ua)) return false;
  return /Safari/i.test(ua);
}

function readDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_MS;
  } catch {
    return false;
  }
}

function writeDismissed() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
}

function clearDismissed() {
  try { localStorage.removeItem(DISMISS_KEY); } catch {}
}

// --- Component -------------------------------------------------------------------

/**
 * InstallAppBanner
 *
 * A polished, responsive PWA install experience for DueMate.
 * - Android/Desktop Chrome/Edge: listens to `beforeinstallprompt`, shows Install button.
 * - iOS Safari: shows step-by-step "Add to Home Screen" instructions (Apple has no API).
 * - Hides itself entirely when app is already installed / running standalone.
 * - Dismissal persists for 7 days.
 */
export default function InstallAppBanner() {
  const [installed, setInstalled] = useState(() => isStandalone());
  const [dismissed, setDismissed] = useState(() => readDismissed());
  const [deferred, setDeferred] = useState(null);
  const [showIOSDetails, setShowIOSDetails] = useState(false);

  const iosBanner = useMemo(() => isIOSSafari() && !installed, [installed]);

  // beforeinstallprompt + appinstalled + display-mode listeners
  useEffect(() => {
    if (installed) return;

    const bip = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const ai = () => {
      setInstalled(true);
      setDeferred(null);
      clearDismissed();
      celebrateInstall();
    };
    window.addEventListener("beforeinstallprompt", bip);
    window.addEventListener("appinstalled", ai);

    // React if user installs via browser UI while page is open
    let mm = null;
    let onChange = null;
    if (window.matchMedia) {
      mm = window.matchMedia("(display-mode: standalone)");
      onChange = (ev) => { if (ev.matches) setInstalled(true); };
      if (mm.addEventListener) mm.addEventListener("change", onChange);
      else if (mm.addListener) mm.addListener(onChange);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", bip);
      window.removeEventListener("appinstalled", ai);
      if (mm && onChange) {
        if (mm.removeEventListener) mm.removeEventListener("change", onChange);
        else if (mm.removeListener) mm.removeListener(onChange);
      }
    };
  }, [installed]);

  // Nothing to render when installed, dismissed, or no install path available
  if (installed || dismissed) return null;
  const canInstallNatively = !!deferred;
  if (!canInstallNatively && !iosBanner) return null;

  const handleInstall = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice && choice.outcome === "accepted") {
        clearDismissed();
      }
    } catch {}
    setDeferred(null);
  };

  const handleDismiss = () => {
    writeDismissed();
    setDismissed(true);
  };

  // --- iOS Safari banner ------------------------------------------------------
  if (iosBanner) {
    return (
      <div
        data-testid="install-banner-ios"
        className="nb-card bg-[#FFD166] px-3 py-3 sm:px-4 sm:py-3 mb-4 flex flex-col gap-2"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="/icons/icon-192.png"
              alt="DueMate"
              className="w-10 h-10 rounded-md border-2 border-black bg-white flex-shrink-0"
            />
            <div className="min-w-0">
              <h3 className="font-heading font-black text-sm sm:text-base tracking-tight leading-tight">
                Install DueMate on your iPhone
              </h3>
              <p className="text-[11px] sm:text-xs font-semibold text-[#111]/80 leading-snug">
                Tap Share <ShareNetwork size={11} weight="bold" className="inline align-[-1px] mx-0.5" /> in Safari, then “Add to Home Screen”.
              </p>
            </div>
          </div>
          <button
            data-testid="install-banner-dismiss"
            onClick={handleDismiss}
            aria-label="Dismiss install banner"
            className="nb-btn bg-white px-2 py-1.5 flex-shrink-0"
          >
            <X size={12} weight="bold" />
          </button>
        </div>

        {showIOSDetails ? (
          <ol className="text-[12px] font-semibold list-decimal pl-5 space-y-0.5 bg-white/60 border-2 border-black rounded-md px-3 py-2">
            <li>Tap the <b>Share</b> button at the bottom of Safari.</li>
            <li>Scroll and select <b>“Add to Home Screen”</b>.</li>
            <li>Tap <b>Add</b> — DueMate will appear on your home screen.</li>
          </ol>
        ) : null}

        <div className="flex items-center gap-2">
          <button
            data-testid="install-banner-ios-toggle"
            onClick={() => setShowIOSDetails((v) => !v)}
            className="nb-btn bg-[#111] text-white text-xs px-3 py-1.5"
          >
            {showIOSDetails ? "Got it" : "Show steps"}
          </button>
        </div>
      </div>
    );
  }

  // --- Android / Desktop Chrome / Edge banner --------------------------------
  return (
    <div
      data-testid="install-banner"
      className="nb-card bg-[#FFD166] px-3 py-3 sm:px-4 sm:py-3 mb-4 flex items-center justify-between gap-3"
    >
      <div className="flex items-center gap-3 min-w-0">
        <img
          src="/icons/icon-192.png"
          alt="DueMate"
          className="w-10 h-10 rounded-md border-2 border-black bg-white flex-shrink-0"
        />
        <div className="min-w-0">
          <h3 className="font-heading font-black text-sm sm:text-base tracking-tight leading-tight">
            Install DueMate
          </h3>
          <p className="text-[11px] sm:text-xs font-semibold text-[#111]/80 leading-snug truncate">
            Install DueMate for quick access from your device.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          data-testid="install-banner-install"
          onClick={handleInstall}
          className="nb-btn bg-[#4361EE] text-white text-xs sm:text-sm px-3 py-1.5 flex items-center gap-1"
        >
          <DownloadSimple size={12} weight="bold" />
          <span>Install App</span>
        </button>
        <button
          data-testid="install-banner-dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss install banner"
          className="nb-btn bg-white px-2 py-1.5"
        >
          <X size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}
